/**
 * From a support e-mail to a group ID.
 *
 * Every recovery procedure begins with a group ID, and the only way to get one
 * was to already have it. A support request arrives as "I'm nikodem@… and we've
 * lost our photos", and nothing here turned that into an identifier — so the
 * documented process could not be started, however carefully it was written.
 *
 * Exact address only, never a fragment. A prefix search over a user table is a
 * way to enumerate the people using an invite-only service, and this panel
 * refuses that elsewhere on purpose ("names aren't searchable here").
 *
 * What it shows beside each group is whether that group can be recovered at
 * all. It is the next question in every single case, and finding out afterwards
 * — halfway through the procedure — is the cruellest possible moment.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { errorMessage, trpc } from "../../lib/trpc.js";
import { Badge } from "../../components/ui/Badge.js";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import "./FindByEmail.css";

export function FindByEmail({ onPick }: { onPick: (groupId: string) => void }) {
  const [draft, setDraft] = useState("");
  const [email, setEmail] = useState<string | null>(null);

  const found = useQuery({
    ...trpc.admin.findGroupsForEmail.queryOptions({ email: email ?? "" }),
    enabled: Boolean(email),
    retry: false,
  });

  return (
    <div className="fc-byemail">
      <form
        className="fc-admin__search"
        onSubmit={(event) => {
          event.preventDefault();
          setEmail(draft.trim());
        }}
      >
        <Field
          label="Or find by e-mail"
          hint="The address on the support request. The whole address — partial ones are not searchable."
          className="fc-admin__search-field"
        >
          <Input
            type="email"
            value={draft}
            autoComplete="off"
            spellCheck={false}
            placeholder="somebody@example.com"
            onChange={(event) => setDraft(event.target.value)}
          />
        </Field>
        <Button
          type="submit"
          variant="secondary"
          disabled={!draft.includes("@")}
        >
          Find groups
        </Button>
      </form>

      {found.isFetching ? (
        <Skeleton height="120px" radius="md" />
      ) : found.isError ? (
        <p className="fc-admin__error" role="alert">
          {errorMessage(found.error)}
        </p>
      ) : found.data ? (
        <>
          <p className="fc-byemail__who">
            <strong>{found.data.user.displayName}</strong> ({found.data.user.username})
            {" · "}
            {found.data.user.emailVerified
              ? "address confirmed"
              : "address never confirmed"}
            {" · "}
            {/*
              Whether this account can be sealed *to* at all. An account made
              before keys existed, or one whose keyring was never set up, cannot
              be given a group back until it has one — and that is worth knowing
              before rather than after running the offline tool.
            */}
            {found.data.user.hasPublicKey
              ? "has a public key"
              : "no public key yet"}
          </p>

          {found.data.memberships.length === 0 ? (
            <p className="fc-admin__hint">That account is in no groups.</p>
          ) : (
            <ul className="fc-byemail__list" role="list">
              {found.data.memberships.map((membership) => (
                <li key={membership.groupId}>
                  <button
                    type="button"
                    onClick={() => onPick(membership.groupId)}
                  >
                    <span className="fc-byemail__name">
                      {membership.emoji ? `${membership.emoji} ` : ""}
                      {membership.name}
                    </span>
                    <code className="fc-byemail__id">{membership.groupId}</code>
                  </button>

                  <span className="fc-byemail__tags">
                    <Badge variant="neutral">{membership.role}</Badge>
                    {membership.leftAt && <Badge variant="neutral">left</Badge>}
                    <Badge variant={membership.escrowed ? "accent" : "danger"}>
                      {membership.escrowed ? "recoverable" : "no escrow copy"}
                    </Badge>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </div>
  );
}
